import React, { useEffect, useRef, MouseEvent, useState } from 'react';
import AnimatedBackground from '../components/AnimatedBackground';
import styles from './index.module.scss';
import Header from '../header';
import RippleButton from '../components/RippleButton';
import WeCom from '../../assets/images/wecom.jpg';
import ImageDisplayModal from '../components/ImageDisplayModal';
import { useScroll } from '@react-three/drei';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface PricingCardProps {
  planIcon: string;
  planName: string;
  planPrice: { original: string, promotional: string } | string;
  planPeriod: string;
  features: FeatureCategory[];
  buttonText: string;
  buttonClass: string;
  isPopular?: boolean;
  particleId: string;
}

interface FeatureCategory {
  title: string;
  items: FeatureItem[];
}

interface FeatureItem {
  icon: string;
  iconClass: string;
  text: string;
}

const PricingCard: React.FC<PricingCardProps> = ({
  planIcon,
  planName,
  planPrice,
  planPeriod,
  features,
  buttonText,
  buttonClass,
  isPopular = false,
  particleId
}) => {
  const particlesRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate()
  const [isWeComVisible, setIsWeComVisible] = useState<boolean>(false);
  const { t } = useTranslation();


  useEffect(() => {
    const createParticles = (count = 15) => {
      const container = particlesRef.current;
      if (!container) return;

      for (let i = 0; i < count; i++) {
        const particle = document.createElement('div');
        particle.className = styles.particle;
        particle.style.left = Math.random() * 100 + '%';
        particle.style.animationDelay = Math.random() * 15 + 's';
        particle.style.animationDuration = (Math.random() * 5 + 10) + 's';

        const colors = ['#00ffff', '#ff00ff', '#ffff00'];
        particle.style.background = colors[Math.floor(Math.random() * colors.length)];

        container.appendChild(particle);
      }
    };

    createParticles();

    return () => {
      if (particlesRef.current) {
        particlesRef.current.innerHTML = '';
      }
    };
  }, []);

  const handleButtonClick = (e: MouseEvent<HTMLButtonElement>) => {
    // 这里可以放置按钮点击后除了波纹效果之外的其他逻辑
    console.log('Button clicked!', buttonClass);
    console.log(buttonText)
    if (planName === '企业定制版') {
      setIsWeComVisible(true)
    } else {
      navigate('/login')
    }

  };

  const handleMouseEnter = () => {
    if (cardRef.current) {
      cardRef.current.style.zIndex = '10';
    }
  };

  const handleMouseLeave = () => {
    if (cardRef.current) {
      cardRef.current.style.zIndex = '1';
    }
  };

  const renderPrice = () => {
    if (typeof planPrice === 'string') {
      // 没有优惠，只显示一个价格
      return <div className={styles.planPrice}>{planPrice}</div>;
    } else {
      // 有优惠，显示原价和活动价
      return (
        <div className={styles.planPrice}>
          <span className={styles.originalPrice}>{planPrice.original}</span>
          <span className={styles.promotionalPrice}>
            {planPrice.promotional}
            <span className={styles.promotionalText}>{t('pricing.special')}</span>
          </span>
        </div>
      );
    }
  }

  return (
    <>
      <div
        className={styles.pricingCard}
        ref={cardRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {isPopular && <div className={styles.popularTag}>{t('pricing.popular')}</div>}
        <div className={styles.cardGlow}></div>
        <div className={styles.particles} ref={particlesRef}></div>

        <div className={styles.planHeader}>
          <div className={styles.planIcon}>{planIcon}</div>
          <h3 className={styles.planName}>{planName}</h3>

          {renderPrice()}

          <div className={styles.planPeriod}>{planPeriod}</div>
        </div>

        <div className={styles.featuresContainer}>
          {features.map((category, categoryIndex) => (
            <div key={categoryIndex} className={styles.featureCategory}>
              <div className={styles.categoryTitle}>{category.title}</div>
              {category.items.map((item, itemIndex) => (
                <div key={itemIndex} className={styles.featureItem}>
                  <div className={`${styles.featureIcon} ${styles[item.iconClass]}`}>
                    {item.icon}
                  </div>
                  <span dangerouslySetInnerHTML={{ __html: item.text }} />
                </div>
              ))}
            </div>
          ))}
        </div>



        <RippleButton
          className={`${styles.ctaButton} ${styles[buttonClass]}`} // 将你原有的样式作为 className 传递
          onClick={handleButtonClick}
          duration={700} // 可选：自定义波纹持续时间
        >
          {buttonText} {/* 将按钮文本作为子元素传递 */}
        </RippleButton>
      </div>
      <ImageDisplayModal
        isVisible={isWeComVisible}
        onClose={() => setIsWeComVisible(false)}
        imageUrl={WeCom}
        imageAlt=""
        imageWidth={300}
        preview={false}
        title="扫码咨询定制"
      />
    </>

  );
};

const PricingPage: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const cards = document.querySelectorAll(`.${styles.pricingCard}`);
      const mouseX = e.clientX;
      const mouseY = e.clientY;

      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const cardX = rect.left + rect.width / 2;
        const cardY = rect.top + rect.height / 2;

        const deltaX = (mouseX - cardX) / rect.width;
        const deltaY = (mouseY - cardY) / rect.height;

        if (Math.abs(deltaX) < 0.5 && Math.abs(deltaY) < 0.5) {
          (card as HTMLElement).style.transform = `
            translateY(-20px) 
            rotateX(${deltaY * 10}deg) 
            rotateY(${deltaX * 10}deg)
            scale(1.02)
          `;
        }
      });
    };

    const handleMouseMoveEvent = (e: Event) => handleMouseMove(e as unknown as MouseEvent);

    document.addEventListener('mousemove', handleMouseMoveEvent);

    return () => {
      document.removeEventListener('mousemove', handleMouseMoveEvent);
    };
  }, []);

  

  const pricingPlans: PricingCardProps[] = [
    {
      planIcon: '🆓',
      planName: t('pricing.planFree.name'),
      planPrice: t('pricing.planFree.price'),
      planPeriod: t('pricing.planFree.period'),
      buttonText: t('pricing.planFree.button'),
      buttonClass: 'btnFree',
      particleId: 'particles1',
      features: [
        {
          title: t('pricing.featureVideo.title'),
          items: [
            { icon: '!', iconClass: 'featureLimited', text: t('pricing.featureVideo.item1') + ' <span class="' + styles.limitation + '">'+ t('pricing.featureVideo.item1_limit') + '</span>' },
            { icon: '!', iconClass: 'featureLimited', text: t('pricing.featureVideo.item2') + ' <span class="' + styles.limitation + '">' + t('pricing.featureVideo.item2_limit') + '</span>' },
            { icon: '!', iconClass: 'featureLimited', text: t('pricing.featureVideo.item3') + ' <span class="' + styles.limitation + '">' + t('pricing.featureVideo.item3_limit') + '</span>' },
            { icon: '✓', iconClass: 'featureCheck', text: t('pricing.featureVideo.item4') + ' <span class="' + styles.unlimited + '">' + t('pricing.featureVideo.item4_limit') + '</span>' },
            { icon: '✓', iconClass: 'featureCheck', text: t('pricing.featureVideo.item5') },
            { icon: '✓', iconClass: 'featureCheck', text: t('pricing.featureVideo.item6') },
          ]
        },
        {
          title: t('pricing.featureTemplate.title'),
          items: [
            { icon: '✓', iconClass: 'featureCheck', text: '<span class="' + styles.valueHighlight + '">' + t('pricing.featureTemplate.item1_limit') + '</span> ' + t('pricing.featureTemplate.item1') },
          ]
        },

      ]
    },
    {
      planIcon: '⚡',
      planName: t('pricing.planAdvanced.name'),
      planPrice: {
        original: t('pricing.planAdvanced.price'),
        promotional: t('pricing.planAdvanced.promotional')
      },
      planPeriod: t('pricing.planAdvanced.period'),
      buttonText: t('pricing.planAdvanced.button'),
      buttonClass: 'btnPrimary',
      particleId: 'particles2',
      features: [
        {
          title: t('pricing.featureVideo.title'),
          items: [
            { icon: '✓', iconClass: 'featureCheck', text: t('pricing.featureVideo.item7') + ' <span class="' + styles.unlimited + '">' + t('pricing.featureVideo.item7_limit') + '</span>' },
            { icon: '✓', iconClass: 'featureCheck', text: '<span class="' + styles.unlimited + '">' + t('pricing.featureVideo.item8_limit') + '</span> ' + t('pricing.featureVideo.item8') },
            { icon: '✓', iconClass: 'featureCheck', text: '<span class="' + styles.unlimited + '">' + t('pricing.featureVideo.item9_limit') + '</span> ' + t('pricing.featureVideo.item9')},
            { icon: '✓', iconClass: 'featureCheck', text: t('pricing.featureVideo.item4') + ' <span class="' + styles.unlimited + '">' + t('pricing.featureVideo.item4_limit') + '</span>' },
            { icon: '✓', iconClass: 'featureCheck', text: t('pricing.featureVideo.item10') },
            { icon: '✓', iconClass: 'featureCheck', text: t('pricing.featureVideo.item11') },
            { icon: '✓', iconClass: 'featureCheck', text: t('pricing.featureVideo.item12') },
          ]
        },
        {
          title: t('pricing.featureTemplate.title'),
          items: [
            { icon: '✓', iconClass: 'featureCheck', text: '<span class="' + styles.valueHighlight + '">' + t('pricing.featureTemplate.item1_limit') + '</span> ' + t('pricing.featureTemplate.item1') },
          ]
        },
      ]
    },
    {
      planIcon: '🚀',
      planName: t('pricing.planPro.name'),
      planPrice: {
        original: t('pricing.planPro.price'),
        promotional:  t('pricing.planPro.promotional')
      },
      planPeriod: t('pricing.planPro.period'),
      buttonText: t('pricing.planPro.button'),
      buttonClass: 'btnPopular',
      isPopular: true,
      particleId: 'particles3',
      features: [
        {
          title: t('pricing.featurePremium.title'),
          items: [
            { icon: '★', iconClass: 'featurePremium', text: t('pricing.featurePremium.item1') }
          ]
        },
        {
          title: t('pricing.featureProEnhanced.title'),
          items: [
            { icon: '★', iconClass: 'featurePremium', text: '<span class="' + styles.valueHighlight + '">' + t('pricing.featureProEnhanced.item1_limit') + '</span> ' + t('pricing.featureProEnhanced.item1') },
            { icon: '★', iconClass: 'featurePremium', text: t('pricing.featureProEnhanced.item2') },
          ]
        }
      ]
    },
    {
      planIcon: '🏢',
      planName: t('pricing.planEnterprise.name'),
      planPrice: t('pricing.planEnterprise.price'),
      planPeriod: t('pricing.planEnterprise.period'),
      buttonText: t('pricing.planEnterprise.button'),
      buttonClass: 'btnEnterprise',
      particleId: 'particles4',
      features: [
        {
          title: t('pricing.featureEnterprise.title'),
          items: [
            { icon: '★', iconClass: 'featurePremium', text: t('pricing.featureEnterprise.item1') }
          ]
        },
        {
          title: t('pricing.featureEnterpriseService.title'),
          items: [
            { icon: '★', iconClass: 'featurePremium', text: '<span class="' + styles.unlimited + '">' + t('pricing.featureEnterpriseService.item1_limit') + '</span> ' +  t('pricing.featureEnterpriseService.item1') },
            { icon: '★', iconClass: 'featurePremium', text: t('pricing.featureEnterpriseService.item2') },
            { icon: '★', iconClass: 'featurePremium', text: t('pricing.featureEnterpriseService.item3') },
            { icon: '★', iconClass: 'featurePremium', text: t('pricing.featureEnterpriseService.item4') },
            { icon: '★', iconClass: 'featurePremium', text: t('pricing.featureEnterpriseService.item5') },
          ]
        }
      ]
    }
  ];

  return (
    <div>
      <AnimatedBackground />
      <Header />
      <div className={styles.container} ref={containerRef}>
        <div className={styles.header}>
          <h1>{t('pricing.title')}</h1>
          <p>{t('pricing.sub_title')}</p>
        </div>

        <div className={styles.pricingContainer}>
          {pricingPlans.map((plan, index) => (
            <PricingCard key={index} {...plan} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PricingPage;